"use client"

import { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { AuthContext } from "../../../contexts/AuthContext"
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
  CurrencyRupeeIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline"

function CampaignHistory() {
  const navigate = useNavigate()
  const { fetchAllUsers, fetchCampaignHistory, exportCampaignHistory, getUserCampaigns, getCampaignMessages, getQueueStats, processQueuedCampaigns, user } = useContext(AuthContext)

  // State management
  const [users, setUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState("")
  const [history, setHistory] = useState([])
  const [campaignSummary, setCampaignSummary] = useState({})
  const [campaigns, setCampaigns] = useState([])
  const [campaignPagination, setCampaignPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 })
  const [campaignLoading, setCampaignLoading] = useState(false)
  const [queueStats, setQueueStats] = useState({})
  const [queueProcessing, setQueueProcessing] = useState(false)
  const [campaignMessages, setCampaignMessages] = useState([])
  const [selectedCampaignId, setSelectedCampaignId] = useState(null)
  const [campaignMessagesLoading, setCampaignMessagesLoading] = useState(false)
  const [campaignMessagesPagination, setCampaignMessagesPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 })
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(50)

  // Filters
  const [filters, setFilters] = useState({
    messageType: "",
    status: "",
    startDate: "",
    endDate: "",
    campaignId: "",
  })

  // Summary stats
  const [summary, setSummary] = useState({})
  const [messageTypeStats, setMessageTypeStats] = useState({})

  // Load users on component mount
  useEffect(() => {
    loadUsers()
  }, [])

  // Role guard: only superadmin/admin/support access this page
  useEffect(() => {
    if (user && !["superadmin", "admin", "support"].includes(user.role)) {
      navigate("/")
    }
  }, [user, navigate])

  // Load history when user or filters change
  useEffect(() => {
    if (selectedUserId || (user && ["superadmin", "admin", "support"].includes(user.role))) {
      loadHistory()
    }
  }, [selectedUserId, currentPage, recordsPerPage, filters])

  // Load campaign metrics
  useEffect(() => {
    if (selectedUserId || (user && ["superadmin", "admin", "support"].includes(user.role))) {
      loadCampaigns()
      loadQueueStats()
    }
  }, [selectedUserId, currentPage, recordsPerPage, filters])

  const loadUsers = async () => {
    try {
      const usersData = await fetchAllUsers()
      setUsers(usersData)
    } catch (error) {
      console.error("Error loading users:", error)
    }
  }

  const loadHistory = async () => {
    setLoading(true)
    try {
      const result = await fetchCampaignHistory(selectedUserId || null, filters, currentPage, recordsPerPage)

      if (result.success) {
        setHistory(result.history)
        setTotalPages(result.pagination.totalPages)
        setTotalRecords(result.pagination.totalRecords)
        setSummary(result.summary)
        setMessageTypeStats(result.messageTypeStats)
      }
    } catch (error) {
      console.error("Error loading history:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadCampaigns = async () => {
    setCampaignLoading(true)
    try {
      const result = await getUserCampaigns({ userId: selectedUserId || "", page: currentPage, limit: recordsPerPage, status: filters.status || "" })
      if (result.success) {
        setCampaigns(result.campaigns)
        setCampaignSummary(result.summary)
        setCampaignPagination(result.pagination)
      }
    } catch (error) {
      console.error("Error loading campaigns:", error)
    } finally {
      setCampaignLoading(false)
    }
  }

  const loadCampaignMessages = async (campaignId, page = 1, limit = 50) => {
    if (!campaignId) return
    setCampaignMessagesLoading(true)
    try {
      const result = await getCampaignMessages(campaignId, page, limit)
      if (result.success) {
        setCampaignMessages(result.data || [])
        setCampaignMessagesPagination(result.pagination || { page, limit, total: 0, pages: 0 })
        setSelectedCampaignId(campaignId)
      } else {
        setCampaignMessages([])
        setCampaignMessagesPagination({ page, limit, total: 0, pages: 0 })
      }
    } catch (error) {
      console.error("Error loading campaign messages:", error)
      setCampaignMessages([])
      setCampaignMessagesPagination({ page, limit, total: 0, pages: 0 })
    } finally {
      setCampaignMessagesLoading(false)
    }
  }

  const loadQueueStats = async () => {
    try {
      const result = await getQueueStats()
      setQueueStats(result.queueStats || {})
    } catch (error) {
      console.error("Error loading queue stats:", error)
    }
  }

  const runQueuedCampaigns = async () => {
    setQueueProcessing(true)
    try {
      await processQueuedCampaigns()
      await Promise.all([loadCampaigns(), loadQueueStats()])
    } catch (error) {
      console.error("Error running queued campaigns:", error)
    } finally {
      setQueueProcessing(false)
    }
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    // Implement search logic here if needed
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const clearFilters = () => {
    setFilters({
      messageType: "",
      status: "",
      startDate: "",
      endDate: "",
      campaignId: "",
    })
    setCurrentPage(1)
  }

  const handleExport = async (format = "excel") => {
    try {
      await exportCampaignHistory(selectedUserId || null, filters, format)
    } catch (error) {
      console.error("Error exporting:", error)
    }
  }

  const refreshAll = async () => {
    await Promise.all([loadHistory(), loadCampaigns()])
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleViewDetails = (record) => {
    setSelectedRecord(record)
    setShowDetailModal(true)
  }

  const formatCredits = (value) => {
    if (value === null || value === undefined || value === "") return "0"
    const num = Number(value)
    if (Number.isNaN(num)) return value
    let formatted = num.toFixed(5)
    // Remove trailing zeros after decimal point, but keep at least one digit after decimal if needed
    if (formatted.indexOf('.') >= 0) {
      formatted = formatted.replace(/\.0+$/, '') // strip .00000
      formatted = formatted.replace(/(\d\.\d*?[1-9])0+$/, '$1') // strip trailing zeros after nonzero
    }
    return formatted
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case "failed":
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      case "refunded":
        return <ExclamationCircleIcon className="w-5 h-5 text-yellow-500" />
      default:
        return <ExclamationCircleIcon className="w-5 h-5 text-gray-500" />
    }
  }

  const getMessageTypeColor = (type) => {
    switch (type) {
      case "marketing":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
      case "utility":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
      case "authentication":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
    }
  }

  const filteredHistory = history.filter(
    (record) =>
      searchTerm === "" ||
      record.campaignName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.recipientNumber?.includes(searchTerm) ||
      record.messageId?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 bg-gray-50 dark:bg-gray-900 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-none">
        {/* Header */}
        <div className="rounded-2xl p-3 mb-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <ChartBarIcon className="w-8 h-8 text-green-600" />
                Campaign Cut History
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                View detailed campaign credit deduction history and analytics
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={runQueuedCampaigns}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold"
                disabled={queueProcessing}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {queueProcessing ? "Retrying Queue..." : "Retry Queued"}
              </button>
              <button
                onClick={refreshAll}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={() => handleExport("excel")}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Export 
              </button>
              <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              
            </h3>
            <select
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username || user.email} ({user.role})
                </option>
              ))}
            </select>
          </div>
            </div>
          </div>
        </div>

        {/* User Selection & Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          {[
            { title: "Ledger Records", value: summary.totalMessages || 0 },
            { title: "Campaign Charged", value: summary.totalCharged || 0, credits: true },
            { title: "Refunded", value: summary.totalRefunded || 0, credits: true, sub: `${summary.refundCount || 0} refunds` },
            { title: "Net Credit Cut", value: summary.totalCreditsDeducted || 0, credits: true },
          ].map((card) => (
            <div key={card.title} className="rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300">{card.title}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {card.credits ? `₹${formatCredits(card.value)}` : card.value}
              </p>
              {card.sub && <p className="text-xs text-gray-500 dark:text-gray-400">{card.sub}</p>}
            </div>
          ))}
        </div>

        {/* Live campaign status summary (single row compact) */}
        <div className="flex gap-2 overflow-x-auto mb-4 py-1">
          {[
            { title: "Total Campaigns", value: campaignSummary.totalCampaigns || 0 },
            { title: "Queued", value: campaignSummary.queue || 0 },
            { title: "Sending", value: campaignSummary.sending || 0 },
            { title: "Completed", value: campaignSummary.completed || 0 },
            { title: "Total Messages", value: campaignSummary.totalMessages || 0 },
            { title: "Sent", value: campaignSummary.sent || 0 },
            { title: "Delivered", value: campaignSummary.delivered || 0 },
            { title: "Read", value: campaignSummary.read || 0 },
            { title: "Failed", value: campaignSummary.failedMessages || 0 },
            { title: "Pending", value: campaignSummary.pending || 0 },
            { title: "Queue Msgs", value: campaignSummary.queuedMessages || 0 },
            { title: "Credits Used", value: campaignSummary.creditsUsed || 0 },
          ].map((card) => (
            <div key={card.title} className="min-w-[140px] rounded-lg px-2 py-1 border border-gray-200 dark:border-gray-700 bg-transparent shadow-none">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300">{card.title}</p>
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {card.title === "Credits Used" ? formatCredits(card.value) : card.value}
              </p>
            </div>
          ))}
        </div>
        {/* Campaign table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-auto mb-6">
          {campaignLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading campaign status...</span>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-600 dark:text-gray-400">No campaign data available.</p>
            </div>
          ) : (
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Queue</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Delivered</th>
                  <th className="px-4 py-3">Read</th>
                  <th className="px-4 py-3">Failed</th>
                  <th className="px-4 py-3">Credits Used</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Completed</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {campaigns.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {c.campaignName || c.templateName || "Untitled"}
                    </td>
                    <td className="px-4 py-3 capitalize">{c.status || "N/A"}</td>
                    <td className="px-4 py-3">{c.status === "queued" ? "Yes" : "No"}</td>
                    <td className="px-4 py-3">{c.stats?.sent || 0}</td>
                    <td className="px-4 py-3">{c.stats?.delivered || 0}</td>
                    <td className="px-4 py-3">{c.stats?.read || 0}</td>
                    <td className="px-4 py-3">{c.stats?.failed || 0}</td>
                    <td className="px-4 py-3 font-semibold text-green-600">₹{formatCredits(c.creditsUsed || 0)}</td>
                    <td className="px-4 py-3">{c.createdAt ? new Date(c.createdAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-3">{c.startedAt ? new Date(c.startedAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-3">{c.completedAt ? new Date(c.completedAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => loadCampaignMessages(c._id)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-semibold"
                      >
                        View Messages
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Campaign message details for selected campaign */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-auto mb-6">
          <div className="p-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign Messages</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{selectedCampaignId ? `Campaign ID: ${selectedCampaignId}` : "Select a campaign to view messages"}</p>
          </div>
          {campaignMessagesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading campaign messages...</span>
            </div>
          ) : campaignMessages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">No messages to show. Select a campaign to load details.</p>
            </div>
          ) : (
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Error</th>
                  <th className="px-4 py-2">Sent At</th>
                  <th className="px-4 py-2">Delivered At</th>
                  <th className="px-4 py-2">Read At</th>
                  <th className="px-4 py-2">Credit</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {campaignMessages.map((m) => (
                  <tr key={m._id || m.messageId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{m.number || "-"}</td>
                    <td className="px-4 py-2 capitalize">{m.status || "-"}</td>
                    <td className="px-4 py-2 text-xs text-red-500">{m.errorMessage || m.errorCode || "-"}</td>
                    <td className="px-4 py-2">{m.sentAt ? new Date(m.sentAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-2">{m.deliveredAt ? new Date(m.deliveredAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-2">{m.readAt ? new Date(m.readAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-2">{formatCredits(m.creditUsed || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {campaignMessagesPagination.total > campaignMessagesPagination.limit && (
            <div className="p-4 flex items-center justify-end gap-2 text-sm text-gray-500">
              <button
                disabled={campaignMessagesPagination.page <= 1}
                onClick={() => loadCampaignMessages(selectedCampaignId, campaignMessagesPagination.page - 1, campaignMessagesPagination.limit)}
                className="px-3 py-1 rounded-lg border border-gray-300"
              >
                Prev
              </button>
              <span>
                Page {campaignMessagesPagination.page} of {campaignMessagesPagination.pages}
              </span>
              <button
                disabled={campaignMessagesPagination.page >= campaignMessagesPagination.pages}
                onClick={() => loadCampaignMessages(selectedCampaignId, campaignMessagesPagination.page + 1, campaignMessagesPagination.limit)}
                className="px-3 py-1 rounded-lg border border-gray-300"
              >
                Next
              </button>
            </div>
          )}
        </div>

      
        {/* Credit cut and refund ledger */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-auto mb-6">
          <div className="p-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Credit Cut & Refund History</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{totalRecords} records</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading ledger...</span>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">No credit or refund history found.</p>
            </div>
          ) : (
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2">Date & Time</th>
                  {!selectedUserId && <th className="px-4 py-2">User</th>}
                  <th className="px-4 py-2">Campaign</th>
                  <th className="px-4 py-2">Recipient</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Campaign Cut</th>
                  <th className="px-4 py-2">Campaign Bal.</th>
                  <th className="px-4 py-2">Refund</th>
                  <th className="px-4 py-2">After Refund</th>
                  <th className="px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredHistory.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2 whitespace-nowrap">{record.createdAt ? new Date(record.createdAt).toLocaleString() : "-"}</td>
                    {!selectedUserId && (
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900 dark:text-white">{record.userName || "N/A"}</div>
                        <div className="text-xs text-gray-500">{record.userEmail || ""}</div>
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900 dark:text-white">{record.campaignName || "N/A"}</div>
                      <div className="text-xs text-gray-500">{record.templateName || ""}</div>
                    </td>
                    <td className="px-4 py-2 font-mono">{record.recipientNumber || "-"}</td>
                    <td className="px-4 py-2 capitalize">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                        record.status === "refunded"
                          ? "bg-amber-100 text-amber-800"
                          : record.status === "failed"
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                      }`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-semibold">₹{formatCredits(record.chargeAmount || 0)}</td>
                    <td className="px-4 py-2">
                      {record.campaignBalanceBefore !== null && record.campaignBalanceBefore !== undefined ? (
                        <div className="text-xs">
                          <div>Old: ₹{formatCredits(record.campaignBalanceBefore)}</div>
                          <div>After: ₹{formatCredits(record.campaignBalanceAfter)}</div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-2 font-semibold text-amber-700">
                      {record.refundAmount ? `₹${formatCredits(record.refundAmount)}` : "-"}
                    </td>
                    <td className="px-4 py-2">
                      {record.status === "refunded" ? (
                        <div className="text-xs">
                          <div>Before: ₹{formatCredits(record.refundBalanceBefore)}</div>
                          <div>After: ₹{formatCredits(record.refundBalanceAfter)}</div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleViewDetails(record)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-semibold"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Records Per Page */}
        

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {totalPages} ({totalRecords} total records)
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        pageNum === currentPage
                          ? "bg-green-600 text-white"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedRecord && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Campaign History Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Campaign Information</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Campaign Name</label>
                        <p className="text-gray-900 dark:text-white">{selectedRecord.campaignName || "N/A"}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Template Name</label>
                        <p className="text-gray-900 dark:text-white">{selectedRecord.templateName || "N/A"}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Message Type</label>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMessageTypeColor(selectedRecord.messageType)}`}
                        >
                          {selectedRecord.messageType}
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Operator</label>
                        <p className="text-gray-900 dark:text-white">{selectedRecord.operator}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Transaction Details</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Credits Deducted</label>
                        <p className="text-gray-900 dark:text-white font-semibold">
                          ₹{selectedRecord.creditsDeducted?.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Price Per Message
                        </label>
                        <p className="text-gray-900 dark:text-white">₹{selectedRecord.pricePerMessage?.toFixed(3)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Balance Before</label>
                        <p className="text-gray-900 dark:text-white">₹{selectedRecord.balanceBefore?.toFixed(2)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Balance After</label>
                        <p className="text-gray-900 dark:text-white">₹{selectedRecord.balanceAfter?.toFixed(2)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</label>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(selectedRecord.status)}
                          <span className="text-gray-900 dark:text-white capitalize">{selectedRecord.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Message Details</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Recipient Number</label>
                        <p className="text-gray-900 dark:text-white font-mono">{selectedRecord.recipientNumber}</p>
                      </div>
                      {selectedRecord.messageId && (
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Message ID</label>
                          <p className="text-gray-900 dark:text-white font-mono text-sm break-all">
                            {selectedRecord.messageId}
                          </p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Created At</label>
                        <p className="text-gray-900 dark:text-white">
                          {new Date(selectedRecord.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-all duration-200 font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CampaignHistory
